import {Component, OnInit} from '@angular/core';
import {MatDialog}         from '@angular/material/dialog';
import {MatSnackBar}       from '@angular/material/snack-bar';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {forkJoin}          from 'rxjs';
import {ApiService}        from '@src/app/shared/services/api.service';
import {DoorType}          from '../../models/door-type.model';
import {DialogComponent}   from '@src/app/shared/components/dialog/dialog.component';
import {DoorTypeFormDialogComponent} from './door-type-form-dialog.component';

@Component({
    standalone: false,
    selector: 'app-door-types',
    templateUrl: './door-types.component.html',
    styleUrls: ['./door-types.component.scss']
})
export class DoorTypesComponent implements OnInit
{
    public displayedColumns: string[] = ['drag', 'title', 'inspection_frequency_months', 'notify_days_before', 'default', 'actions'];
    public doorTypes: DoorType[] = [];
    public isLoading = false;

    public constructor(
        private apiService: ApiService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
    )
    {
    }

    public ngOnInit(): void
    {
        this.loadDoorTypes();
    }

    public loadDoorTypes(): void
    {
        this.isLoading = true;
        this.apiService.list('door-types', 1, 100).subscribe({
            next: response => {
                this.doorTypes = (response?.data ?? []).slice().sort((a: DoorType, b: DoorType) => a.sort_order - b.sort_order);
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.snackBar.open('Failed to load door types.', 'Ok', {duration: 3500});
            }
        });
    }

    public openDoorTypeDialog(doorType: DoorType | null = null): void
    {
        const dialogRef = this.dialog.open(DoorTypeFormDialogComponent, {
            width: '620px',
            maxWidth: 'calc(100vw - 32px)',
            panelClass: 'mobile-width',
            disableClose: true,
            data: {doorType, sortOrder: this.doorTypes.length},
        });

        dialogRef.afterClosed().subscribe(saved => {
            if (saved) {
                this.loadDoorTypes();
                this.snackBar.open(doorType ? 'Door type updated.' : 'Door type created.', 'Ok', {duration: 2500});
            }
        });
    }

    public confirmDelete(doorType: DoorType): void
    {
        const confirmDialogRef = this.dialog.open(DialogComponent, {
            data: {
                title: 'Delete Door Type',
                message: `Are you sure you want to delete "${doorType.title}"? It will stop being offered on new surveys, but existing recorded entries keep it.`,
                type: 'danger-outline',
                confirmButtonLabel: 'Delete'
            }
        });

        confirmDialogRef.afterClosed().subscribe(confirm => {
            if (confirm) {
                this.apiService.delete('door-types', doorType.id).subscribe({
                    next: () => {
                        this.loadDoorTypes();
                        this.snackBar.open('Door type deleted.', 'Ok', {duration: 2500});
                    },
                    error: response => {
                        const message = response?.error?.message ?? 'Failed to delete door type.';
                        this.snackBar.open(message, 'Ok', {duration: 4000});
                    }
                });
            }
        });
    }

    public drop(event: CdkDragDrop<DoorType[]>): void
    {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        moveItemInArray(this.doorTypes, event.previousIndex, event.currentIndex);

        const changed = this.doorTypes
            .map((doorType, index) => ({doorType, index}))
            .filter(({doorType, index}) => doorType.sort_order !== index);

        if (!changed.length) {
            return;
        }

        changed.forEach(({doorType, index}) => { doorType.sort_order = index; });

        forkJoin(changed.map(({doorType, index}) => this.apiService.update('door-types', doorType.id, {sort_order: index})))
            .subscribe({
                error: () => {
                    this.snackBar.open('Failed to save the new order.', 'Ok', {duration: 3500});
                    this.loadDoorTypes();
                }
            });
    }
}
